// @ts-nocheck
import Router, {
  createRouter,
  createWebHistory,
  RouteRecordRaw,
} from "vue-router";
import Logout from "./views/Logout.vue";
import useractivation from "./views/useractivation.vue";
import absenceSettings from "./modules/absence/settings";
import actualPlanSettings from "./modules/actualplan/settings";
import approveworkhoursSettings from "./modules/approveworkhours/settings";
import dashboardSettings from "./modules/dashboard/settings";
import employeeSettings from "./modules/employee/settings";
import employeesSettings from "./modules/employees/settings";
import planlaegningSettings from "./modules/planlaegning/settings";
import poolsSettings from "./modules/pools/settings";
import renewEmploymentSettings from "./modules/renewEmployment/settings";
import reportsSettings from "./modules/report/settings";
import printSettings from "./modules/prints/settings";
import salarySettings from "./modules/salary/settings";
import settingsSettings from "./modules/settings/settings";
import staffingOverviewSettings from "./modules/staffingOverview/settings";
import supportSettings from "./modules/Support/settings";
import timeScheduleSettings from "./modules/timeSchedule/settings";

import store from "./store";
import axios, { AxiosResponse } from "axios";
import { EventBus, eEventBus } from "./eventbus";
import { Institution } from "./models/institution-model";
import { addYears, format } from "date-fns";
import { UserApiData } from "./modules/security/models/user-model";
import { useUser } from "@/composables/apiModels/useUser";
import { LAST_SELECTED_INSTITUTION_KEY } from "./constants/storage";

declare module "vue-router" {
  interface RouteMeta {
    // is optional
    title?: string;
    // must be declared by every route
    requiresAuth: boolean;
  }
}

const unauthorized = 401;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const res: AxiosResponse = error.response as AxiosResponse;
    const status: number = (res && res.status) || 0;
    if (status === unauthorized) {
      sessionStorage.removeItem("token");
      history.replaceState(history.state, "", "/");
    }
    return Promise.reject(error);
  },
);

const setRoutes = () => {
  // Collect all module routes as children
  const moduleRoutes: Array<RouteRecordRaw> = [
    ...absenceSettings.routes,
    ...actualPlanSettings.routes,
    ...approveworkhoursSettings.routes,
    ...dashboardSettings.routes,
    ...employeeSettings.routes,
    ...employeesSettings.routes,
    ...planlaegningSettings.routes,
    ...poolsSettings.routes,
    ...renewEmploymentSettings.routes,
    ...reportsSettings.routes,
    ...printSettings.routes,
    ...salarySettings.routes,
    ...settingsSettings.routes,
    ...staffingOverviewSettings.routes,
    ...supportSettings.routes,
    ...timeScheduleSettings.routes,
  ];

  const routes: Array<RouteRecordRaw> = [
    {
      path: "/",
      name: "home",
      component: () => import("./views/Start.vue"),
      meta: { requiresAuth: false, title: "Startside" },
    },
    {
      path: "/logout",
      name: "logout",
      component: Logout,
      meta: { requiresAuth: false, title: "Logout" },
    },
    {
      path: "/useractivation",
      name: "useractivation",
      component: useractivation,
      meta: { requiresAuth: false, title: "Aktiver Unilogin" },
    },
    {
      path: "/module",
      name: "layout",
      component: () => import("./views/_Layout.vue"),
      meta: { requiresAuth: true },
      children: moduleRoutes,
    },
  ];

  return createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: routes,
  });
};

const router: Router.Router = setRoutes();

// Helper function to handle institution switching based on localStorage
const switchToLastSelectedInstitution = async (
  user: UserApiData,
): Promise<UserApiData> => {
  const lastSelectedInstitution = localStorage.getItem(
    LAST_SELECTED_INSTITUTION_KEY,
  );
  // If there's a saved institution and id is in selectable_institutions, switch to it
  if (
    lastSelectedInstitution &&
    user.selectable_institutions.some(
      (inst) => inst.id.toString() === lastSelectedInstitution,
    )
  ) {
    const savedInstitution = user.selectable_institutions.find(
      (inst) => inst.id.toString() === lastSelectedInstitution,
    );
    if (
      savedInstitution &&
      savedInstitution.code !== user.selected_institution?.code
    ) {
      // Switch to the saved institution
      await store.dispatch("security/selectInstitution", {
        institutionCode: savedInstitution.code,
        role: savedInstitution.role,
      });
      // Get updated user data with the new institution context
      return await store.dispatch("security/getUser", true);
    }
  }
  return user;
};

router.beforeEach(async (to) => {
  EventBus.emit(eEventBus.changeurlpath, to.path);
  // NOTE: @DJ May be relevant if we get slow refreshes or similar, keep for now.
  // if (to.path === from.path && to.meta.requiresAuth) {
  //   return true;
  // }

  if (
    to.meta.requiresAuth === true &&
    to.path.indexOf("userActivation") === -1
  ) {
    let token = store.getters["security/getAuthenticationToken"];
    if (token === "") {
      token = "aulaLogin";
    }
    if (token !== "") {
      sessionStorage.setItem("afterloginpath", to.path);
      let userError = false;
      await store
        .dispatch("security/getUser", false)
        .then(async (user) => {
          store.commit("security/setToken", token);

          // Check for last selected institution before fetching data
          user = await switchToLastSelectedInstitution(user);
          const targetInstitution = user.selected_institution;

          const institutions: Institution[] = await store.dispatch(
            "institutions/fetchInstitutions",
            { institutionId: targetInstitution.id },
          );
          store.commit("security/setInstitutions", institutions);
          await Promise.all([
            store.dispatch("employees/fetchEmployees", {
              includeHidden: false,
            }),
            store.dispatch("employments/fetchEmployments", {
              from: addYears(new Date(), -1),
              to: addYears(new Date(), 1),
            }),
            store.dispatch("departments/fetchDepartments"),
            store.dispatch("positions/fetchPositions"),
            store.dispatch("positions/fetchPositionCategories"),
            store.dispatch("departments/fetchDepartmentEmployees"),
          ]);
        })
        .catch(() => {
          userError = true;
        });
      if (userError === true) {
        return { name: "home" };
      }
    }
    if (sessionStorage.getItem("token") !== null) {
      return true;
    }
  } else {
    // hack to prevent page refresh when removing #state= from the url
    const hasStateStub = location.href.includes("#state=");
    if (hasStateStub) {
      const userActivation = localStorage.getItem("userActivation");

      const firstIndexOfHashtag = location.href.indexOf("#");
      let newPath: string;
      if (firstIndexOfHashtag) {
        newPath = location.href.substring(0, firstIndexOfHashtag);
        let nextRoute = true;
        await store.dispatch("GetConfig");
        if (userActivation === null) {
          await store
            .dispatch("security/applicationAuthenticate")
            .then(async () => {
              let user: UserApiData = store.getters["security/getUser"]();

              // Check for last selected institution before routing
              user = await switchToLastSelectedInstitution(user);
              const { isManager } = useUser();

              if (isManager(user)) {
                newPath += "module/dashboard";
                history.pushState("", "main", newPath);
                nextRoute = { name: "dashboard" };
              } else {
                const date = format(new Date(), "yyyy-MM-dd");
                newPath +=
                  "module/actualplan/pak/" + user.employee_id + "/date/" + date;
                history.pushState("", "main", newPath);
                nextRoute = {
                  name: "pak",
                  params: { employee_id: user.employee_id, date: date },
                };
              }
            })
            .catch((error) => {
              const err = error["errors"][0].message;
              sessionStorage.setItem("loginError", err);
              EventBus.emit(eEventBus.loginError, err);
              return false;
            });
        } else {
          // User is in activation flow, redirect to useractivation page if not already there
          if (to.name !== "useractivation") {
            // Preserve the hash fragment when redirecting
            const hashFragment = location.hash; // e.g., "#state=..."
            nextRoute = { name: "useractivation", hash: hashFragment };
          } else {
            // Already on useractivation page, let it proceed (keep #state= in URL for component to handle)
            nextRoute = true;
          }
        }
        return nextRoute;
      }
    } else {
      return true;
    }
  }
});

export default router;
